/**
 * Hook del detalle de un Cargador (US-2.2). La red vive en `stationApi`; acá solo
 * el estado de la vista (loading / no encontrado / error / refresh), separable de la
 * presentación — mismo patrón que `useLocation`.
 *
 * Tras un `sync` fallido que devuelve 502 el error queda en `error`; tras uno exitoso
 * el server responde el cargador actualizado y `refresh()` recarga el detalle.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AdminStation } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { stationApi } from '../api/stationApi';

export interface UseStationResult {
  station: AdminStation | null;
  loading: boolean;
  error: string | null;
  /** `true` cuando el backend responde 404 (cargador inexistente). */
  notFound: boolean;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo cargar el cargador.';
}

export function useStation(stationId: string | undefined): UseStationResult {
  const [station, setStation] = useState<AdminStation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!stationId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);

    stationApi
      .get(stationId)
      .then((result) => {
        if (!cancelled) setStation(result);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        if (caught instanceof ApiError && caught.status === 404) {
          setNotFound(true);
        } else {
          setError(toErrorMessage(caught));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [stationId, reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { station, loading, error, notFound, refresh };
}
